import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { Link } from 'react-router-dom'

import { analyser } from '../../analysis/engine'
import { episodesDerives } from '../../analysis/evenements'
import { profilFodmapRecette, type ProfilFodmap } from '../../analysis/fodmap'
import {
  STATUT_LABELS,
  statutsIngredients,
  type StatutAnalyse,
} from '../../analysis/suggestions'
import { db } from '../../db/db'
import { filtrerIngredients, filtrerRecettes, listerIngredients, listerRecettes } from '../../db/repo'
import {
  FODMAP_LEVEL_LABELS,
  INGREDIENT_CATEGORY_LABELS,
  type FodmapLevel,
} from '../../domain/types'
import { Carte, Entete, IconeChevron, IconePlus, Puce, Vide } from '../composants'

const TON_STATUT: Record<StatutAnalyse, 'ok' | 'alerte' | 'danger' | 'neutre' | 'info'> = {
  jamais: 'neutre',
  insuffisant: 'info',
  analyse: 'ok',
  surveiller: 'alerte',
  piste: 'danger',
}

const TON_FODMAP: Record<FodmapLevel, 'ok' | 'alerte' | 'danger' | 'neutre'> = {
  faible: 'ok',
  modere: 'alerte',
  eleve: 'danger',
  inconnu: 'neutre',
}

export function PageBibliotheque() {
  const [vue, setVue] = useState<'recettes' | 'ingredients'>('recettes')
  const [recherche, setRecherche] = useState('')

  const [statutFiltre, setStatutFiltre] = useState<StatutAnalyse | 'tous'>('tous')
  const [fodmapFiltre, setFodmapFiltre] = useState<FodmapLevel | 'tous'>('tous')

  const recettes = useLiveQuery(() => listerRecettes(), [])
  const ingredients = useLiveQuery(() => listerIngredients(), [])

  // Statut d'analyse de chaque ingredient : jamais consomme, trop peu de
  // donnees, analyse, piste... Rend visible, en parcourant la liste, ce que
  // les statistiques peuvent ou non dire de chaque aliment.
  const donnees = useLiveQuery(
    async () => ({
      repas: await db.meals.toArray(),
      sensations: await db.sensations.toArray(),
      selles: await db.stools.toArray(),
      journaux: await db.dailyLogs.toArray(),
      ingredients: await db.ingredients.toArray(),
      recettes: await db.recipes.toArray(),
    }),
    [],
  )

  const statuts = useMemo(() => {
    if (!donnees) return undefined
    const { sensations, selles, ...reste } = donnees
    const resultat = analyser({
      ...reste,
      episodes: episodesDerives(sensations, selles),
      // Les permutations ne servent pas ici et coutent cher : le statut ne
      // depend que des verdicts, pas de leur p-value de permutation.
      options: { permutations: 0 },
    })
    return statutsIngredients(donnees.repas, donnees.ingredients, resultat)
  }, [donnees])

  // Charge FODMAP de chaque recette, pour la parcourir d'un coup d'oeil.
  // Calculee sur `donnees.ingredients` et non sur la liste filtree : un
  // ingredient archive compte toujours dans une recette qui le contient, et
  // l'omettre sous-estimerait silencieusement la charge.
  const profils = useMemo(() => {
    if (!donnees) return undefined
    const m = new Map<string, ProfilFodmap>()
    for (const r of donnees.recettes) m.set(r.id, profilFodmapRecette(r, donnees.ingredients))
    return m
  }, [donnees])

  const recettesFiltrees = useMemo(() => {
    const base = filtrerRecettes(recettes ?? [], recherche)
    if (fodmapFiltre === 'tous') return base
    return base.filter((r) => profils?.get(r.id)?.niveau === fodmapFiltre)
  }, [recettes, recherche, fodmapFiltre, profils])

  const ingredientsFiltres = useMemo(() => {
    const base = filtrerIngredients(ingredients ?? [], recherche)
    if (statutFiltre === 'tous') return base
    return base.filter((i) => statuts?.get(i.id)?.statut === statutFiltre)
  }, [ingredients, recherche, statutFiltre, statuts])

  return (
    <>
      <Entete
        titre="Bibliothèque"
        sousTitre={
          vue === 'recettes'
            ? `${recettes?.length ?? 0} recette(s)`
            : `${ingredients?.length ?? 0} ingrédient(s)`
        }
      />

      <main className="contenu">
        <div className="segments">
          <button
            type="button"
            className="segment"
            aria-pressed={vue === 'recettes'}
            onClick={() => setVue('recettes')}
          >
            Recettes
          </button>
          <button
            type="button"
            className="segment"
            aria-pressed={vue === 'ingredients'}
            onClick={() => setVue('ingredients')}
          >
            Ingrédients
          </button>
        </div>

        <input
          type="search"
          placeholder="Rechercher..."
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          autoComplete="off"
        />

        <Link
          to={vue === 'recettes' ? '/recettes/nouvelle' : '/ingredients/nouveau'}
          className="btn btn--principal btn--large"
        >
          <IconePlus /> {vue === 'recettes' ? 'Nouvelle recette' : 'Nouvel ingrédient'}
        </Link>

        {vue === 'recettes' && (recettes?.length ?? 0) > 0 && (
          <div className="puces">
            {(
              [
                ['tous', 'Toutes'],
                ['faible', 'Peu chargées'],
                ['modere', 'Modérées'],
                ['eleve', 'Chargées'],
              ] as const
            ).map(([cle, libelle]) => (
              <button
                key={cle}
                type="button"
                className="choix"
                aria-pressed={fodmapFiltre === cle}
                onClick={() => setFodmapFiltre(cle)}
              >
                {libelle}
              </button>
            ))}
          </div>
        )}

        {vue === 'recettes' ? (
          recettesFiltrees.length === 0 ? (
            <Vide>
              Aucune recette.
              <br />
              Une recette regroupe des ingrédients que vous consommez souvent ensemble : elle
              rend la saisie d'un repas nettement plus rapide.
            </Vide>
          ) : (
            <ul className="liste">
              {recettesFiltrees.map((r) => (
                <li key={r.id}>
                  <Link to={`/recettes/${r.id}`} className="item">
                    <div className="grandir">
                      <div className="item__principal">{r.nom}</div>
                      <div className="item__secondaire">
                        {r.items.length} ingrédient{r.items.length > 1 ? 's' : ''} ·{' '}
                        {r.portions} portion{r.portions > 1 ? 's' : ''}
                        {(() => {
                          const p = profils?.get(r.id)
                          const f = p?.familles[0]
                          return p && p.niveau !== 'faible' && f ? ` · surtout ${f.libelle.toLowerCase()}` : ''
                        })()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                      {profils?.get(r.id) && !profils.get(r.id)!.nonClassable && (
                        <Puce ton={TON_FODMAP[profils.get(r.id)!.niveau]}>
                          {FODMAP_LEVEL_LABELS[profils.get(r.id)!.niveau]}
                        </Puce>
                      )}
                      {r.favorite && <Puce ton="info">Favorite</Puce>}
                    </div>
                    <span className="chevron">
                      <IconeChevron />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        ) : ingredientsFiltres.length === 0 ? (
          <Vide>Aucun ingrédient ne correspond.</Vide>
        ) : (
          <>
            <div className="puces">
              {(
                [
                  ['tous', 'Tous'],
                  ['piste', 'Pistes'],
                  ['surveiller', 'À surveiller'],
                  ['insuffisant', 'Trop peu de données'],
                  ['analyse', 'Analysés'],
                  ['jamais', 'Jamais consommés'],
                ] as const
              ).map(([cle, libelle]) => (
                <button
                  key={cle}
                  type="button"
                  className="choix"
                  aria-pressed={statutFiltre === cle}
                  onClick={() => setStatutFiltre(cle)}
                >
                  {libelle}
                </button>
              ))}
            </div>

            <Carte plate>
              <p className="tres-doux">
                Les niveaux FODMAP fournis sont <strong>indicatifs</strong>, compilés à partir de
                connaissances publiques générales — ce n'est pas la base Monash, qui est
                propriétaire. Ils servent à regrouper les aliments par famille pour gagner en
                puissance statistique. Vous pouvez tous les corriger.
              </p>
            </Carte>
            <ul className="liste">
              {ingredientsFiltres.map((i) => (
                <li key={i.id}>
                  <Link to={`/ingredients/${i.id}`} className="item">
                    <div className="grandir">
                      <div className="item__principal">{i.nom}</div>
                      <div className="item__secondaire">
                        {INGREDIENT_CATEGORY_LABELS[i.categorie]}
                        {i.famillesFodmap.length > 0 && ` · ${i.famillesFodmap.join(', ')}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                      <Puce ton={TON_FODMAP[i.niveauFodmap]}>
                        {FODMAP_LEVEL_LABELS[i.niveauFodmap]}
                      </Puce>
                      {statuts?.get(i.id) && (
                        <Puce ton={TON_STATUT[statuts.get(i.id)!.statut]}>
                          {statuts.get(i.id)!.statut === 'insuffisant'
                            ? `${statuts.get(i.id)!.repas} repas`
                            : STATUT_LABELS[statuts.get(i.id)!.statut]}
                        </Puce>
                      )}
                    </div>
                    <span className="chevron">
                      <IconeChevron />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    </>
  )
}
